import { capitalize } from "@mui/material"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import React, { useMemo } from "react"

import FileChip from "@/components/upload/components/display/FileChip"
import type { CompanyRegistration, SignedUrl } from "@/graphql/codegen/graphql"
import { CompanyRegistrationType as CompanyRegistrationTypes, FieldGroupEnum } from "@/graphql/codegen/graphql"
import { RegistrationActionsMenu } from "@/screens/CompanySettings/Registration/components/RegistrationActionsMenu"
import type { OpenRegistrationDialogFn } from "@/screens/CompanySettings/Registration/types"
import { normalizeFileData } from "@/screens/CompanySettings/Registration/utils"
import { formatLocationAddress } from "@/utils/utils"

export const useRegistrationColumns = ({
  openRegistrationDialog,
  onRemove,
  fileData,
  tab,
}: {
  fileData: SignedUrl[]
  openRegistrationDialog: OpenRegistrationDialogFn
  onRemove: (registration: CompanyRegistration) => void
  tab: CompanyRegistrationTypes
}): ColumnDef<CompanyRegistration>[] => {
  const normalizedRegistrationFileData = useMemo(
    () => normalizeFileData(fileData, FieldGroupEnum.CompanyRegistrationNumber),
    [fileData]
  )
  const normalizedTaxExemptFileData = useMemo(() => normalizeFileData(fileData, FieldGroupEnum.TaxExempt), [fileData])

  const isFederal = tab === CompanyRegistrationTypes.Federal

  return [
    ...(!isFederal
      ? [
          {
            accessorKey: "location",
            header: "Location",
            cell: ({
              row: {
                original: { location },
              },
            }: CellContext<CompanyRegistration, unknown>) => {
              return formatLocationAddress(location)
            },
            enableSorting: false,
            size: 288,
          },
        ]
      : []),
    {
      accessorKey: "name",
      header: `${capitalize(tab.toLowerCase())} Registration Number`,
      cell: ({
        row: {
          original: { registrationNumber },
        },
      }: CellContext<CompanyRegistration, unknown>) => {
        return registrationNumber
      },
      enableSorting: false,
      size: 200,
    },
    {
      accessorKey: "applicableTo",
      header: "Registration Document",
      cell: ({ row }: CellContext<CompanyRegistration, unknown>) => {
        const data = normalizedRegistrationFileData[row.original.companyRegistrationId!]
        if (!data) return "-"
        return <FileChip file={data} />
      },
      enableSorting: false,
    },
    ...(!isFederal
      ? [
          {
            accessorKey: "taxExemptionNumber",
            header: `${capitalize(tab.toLowerCase())} Tax Exempt`,
            cell: ({ row }: CellContext<CompanyRegistration, unknown>) => row.original.taxExemptionNumber ?? "-",
            enableSorting: false,
          },
          {
            accessorKey: "taxExemptionDocument",
            header: "Tax Exempt Document",
            cell: ({ row }: CellContext<CompanyRegistration, unknown>) => {
              const data = normalizedTaxExemptFileData[row.original.companyRegistrationId!]
              if (!data) return "-"
              return <FileChip file={data} />
            },
            enableSorting: false,
          },
        ]
      : []),
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }: CellContext<CompanyRegistration, unknown>) => (
        <RegistrationActionsMenu
          row={row}
          onEdit={(registration) =>
            openRegistrationDialog({
              selected: registration,
            })
          }
          onRemove={onRemove}
        />
      ),
      size: 50,
      enableSorting: false,
    },
  ]
}

export default useRegistrationColumns
